import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, TrendingUp, TrendingDown } from "lucide-react";
import HelpTooltip from "../common/HelpTooltip";

export default function KpiCard({
  title,
  value,
  secondaryValue,
  secondaryLabel,
  footerLeft,
  footerRight,
  footerNote,
  trend = "flat",
  variant = "default", // "default" | "success" | "danger" | "warning" (now subtle)
  tooltip,
  darkMode = false,
  to = null,
  // Dettaglio opzionale sotto il valore principale: [{ label, value }].
  // Mostra come si arriva al numero grande (es. Dovuto / Versato → Residuo).
  breakdown = null,
}) {
  const navigate = useNavigate();

  // Badge colors for trend - only color the badge, not the entire card
  const getTrendBadgeStyle = () => {
    if (trend === "up") {
      return {
        bg: darkMode ? "bg-emerald-900/30" : "bg-emerald-50",
        text: darkMode ? "text-emerald-400" : "text-emerald-600",
        icon: TrendingUp,
      };
    }
    if (trend === "down") {
      return {
        bg: darkMode ? "bg-rose-900/30" : "bg-rose-50",
        text: darkMode ? "text-rose-400" : "text-rose-600",
        icon: TrendingDown,
      };
    }
    return {
      bg: darkMode ? "bg-neutral-800" : "bg-neutral-100",
      text: darkMode ? "text-neutral-500" : "text-neutral-500",
      icon: null,
    };
  };

  // Subtle accent color for the value based on variant (not the whole card)
  const getValueAccent = () => {
    if (variant === "success") {
      return darkMode ? "text-emerald-400" : "text-emerald-600";
    }
    if (variant === "danger") {
      return darkMode ? "text-rose-400" : "text-rose-600";
    }
    if (variant === "warning") {
      return darkMode ? "text-amber-400" : "text-amber-600";
    }
    return darkMode ? "text-white" : "text-neutral-900";
  };

  const trendStyle = getTrendBadgeStyle();
  const valueColor = getValueAccent();

  const handleClick = () => {
    if (to) navigate(to);
  };

  return (
    <div
      className={`
        rounded-xl border p-6
        transition-all duration-200
        ${darkMode
          ? "bg-neutral-900 border-neutral-800 shadow-md hover:shadow-lg"
          : "bg-white border-neutral-200 shadow-sm hover:shadow-md"
        }
        ${to ? "cursor-pointer" : ""}
      `}
      onClick={handleClick}
      role={to ? "link" : undefined}
      tabIndex={to ? 0 : undefined}
      onKeyDown={to ? (e) => e.key === "Enter" && handleClick() : undefined}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className={`text-sm font-medium flex items-center gap-1 ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
          {title}
          {tooltip && <HelpTooltip text={tooltip} darkMode={darkMode} />}
        </span>
        {to && (
          <ArrowRight
            className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${
              darkMode ? "text-neutral-600" : "text-neutral-500"
            }`}
          />
        )}
      </div>

      {/* Main Value */}
      <div className={`mt-3 font-display text-2xl font-semibold tracking-tight ${valueColor}`}>
        {value}
      </div>

      {/* Secondary Value (if present) */}
      {secondaryValue && (
        <div className="mt-2 flex items-baseline gap-2">
          <span className={`text-sm ${darkMode ? "text-neutral-600" : "text-neutral-500"}`}>
            {secondaryLabel}:
          </span>
          <span className={`text-base font-semibold ${darkMode ? "text-neutral-400" : "text-neutral-700"}`}>
            {secondaryValue}
          </span>
        </div>
      )}

      {/* Dettaglio (opzionale): come si compone il valore principale */}
      {breakdown && breakdown.length > 0 && (
        <div
          className={`mt-3 pt-3 border-t text-xs space-y-1 ${
            darkMode ? "border-neutral-800 text-neutral-400" : "border-neutral-200 text-neutral-600"
          }`}
        >
          {breakdown.map((row) => (
            <div key={row.label} className="flex justify-between gap-2">
              <span>{row.label}</span>
              <span className="font-medium tabular-nums">{row.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Trend Badge */}
      {(footerLeft || footerRight) && (
        <div className="mt-4">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${trendStyle.bg} ${trendStyle.text}`}>
            {trendStyle.icon && <trendStyle.icon className="w-3 h-3" />}
            {footerRight && <span>{footerRight}</span>}
            {footerLeft && footerRight && <span className="opacity-60">·</span>}
            {footerLeft && <span>{footerLeft}</span>}
          </div>
          {footerNote && (
            <p className={`mt-1.5 text-xs ${darkMode ? "text-neutral-600" : "text-neutral-500"}`}>
              {footerNote}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
